/**
 * /dashboard/hr/documents — Employee document store
 * (Phase 3c — 2026-06-01).
 *
 * SUBJECT SELECTION (whose document store is shown):
 *  - Default: the caller's OWN store. GET /hr/documents with no
 *    user_id resolves to the caller server-side; an employee only ever
 *    legitimately sees their own (employee-visibility) docs and the
 *    server filters that — there is NO client-side visibility logic.
 *  - HR admins additionally get an employee picker to view any
 *    employee's store (GET /hr/documents?user_id=N). The employee
 *    directory itself is HR-only (the picker 403s for non-HR and the
 *    self view is the only one offered then). A manager viewing a
 *    report's docs would also be authorized server-side, but the
 *    directory needed to PICK that report is HR-gated, so we honestly
 *    expose the picker only to HR and otherwise show "your documents".
 *
 * Upload targets the chosen subject; visibility is a server-enforced
 * security field. Delete is HR-admin-only (RBAC server-side; control
 * shown only to HR in DocumentList).
 *
 * Migration note (2026-06-01, Next.js → Vite): port of
 * frontend/src/app/(dashboard)/dashboard/hr/documents/page.tsx +
 * layout.tsx, flattened into one route that uses HrModuleShell. Swaps:
 *   - "use client" dropped
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useHrRoles } from "@/components/hr/use-hr-roles";
import { useEmployees } from "@/hooks/use-hr";
import { HrFeatureGate } from "@/components/hr/hr-feature-gate";
import { HrModuleShell } from "@/components/shared/hr-module-shell";
import { DocumentList } from "@/components/hr/document-list";
import { DocumentUploadDialog } from "@/components/hr/document-upload-dialog";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/hr/documents",
)({
  component: HrDocumentsRoute,
});

function HrDocumentsRoute() {
  const { user } = useAuth();
  const { isHrAdmin } = useHrRoles();
  const selfId = user?.user_id;

  // HR picks a subject; everyone else is implicitly their own store.
  const [subjectId, setSubjectId] = useState<number | undefined>(
    undefined,
  );
  const effectiveSubject = isHrAdmin
    ? (subjectId ?? selfId)
    : selfId;

  const employeesQ = useEmployees(isHrAdmin);
  const subjectIsSelf = effectiveSubject === selfId;
  const subjectName = (() => {
    if (subjectIsSelf) return "your";
    const e = employeesQ.data?.employees.find(
      (x) => x.id === effectiveSubject,
    );
    return e ? `${e.first_name} ${e.last_name}'s` : "this employee's";
  })();

  return (
    <HrModuleShell
      title="Documents"
      description={
        isHrAdmin
          ? "Employee document store. Pick an employee, or manage your own."
          : "Your document store — contracts, payslips and more."
      }
      actions={
        effectiveSubject != null ? (
          <DocumentUploadDialog
            subjectUserId={effectiveSubject}
            subjectLabel={subjectName}
          />
        ) : undefined
      }
    >
      <HrFeatureGate>
        {isHrAdmin && (
          <Card className="mb-4">
            <CardContent className="flex flex-wrap items-center gap-3 py-4">
              <span className="text-sm font-medium text-gray-900">
                Viewing
              </span>
              <Select
                value={
                  effectiveSubject != null
                    ? String(effectiveSubject)
                    : undefined
                }
                onValueChange={(v) => setSubjectId(Number(v))}
              >
                <SelectTrigger className="w-72">
                  <SelectValue placeholder="Select an employee" />
                </SelectTrigger>
                <SelectContent>
                  {selfId != null && (
                    <SelectItem value={String(selfId)}>
                      My documents
                    </SelectItem>
                  )}
                  {employeesQ.isError && (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">
                      {toApiError(employeesQ.error).message}
                    </div>
                  )}
                  {(employeesQ.data?.employees ?? [])
                    .filter((e) => e.is_active && e.id !== selfId)
                    .sort((a, b) =>
                      `${a.first_name} ${a.last_name}`.localeCompare(
                        `${b.first_name} ${b.last_name}`,
                      ),
                    )
                    .map((e) => (
                      <SelectItem key={e.id} value={String(e.id)}>
                        {e.first_name} {e.last_name}
                        {e.employee_id ? ` · ${e.employee_id}` : ""}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        )}

        {effectiveSubject != null ? (
          <DocumentList subjectUserId={effectiveSubject} />
        ) : (
          <Card>
            <CardContent className="py-12 text-center text-sm text-muted-foreground">
              Could not resolve your account. Try reloading.
            </CardContent>
          </Card>
        )}
      </HrFeatureGate>
    </HrModuleShell>
  );
}
